import React, {
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import {
  useAuth,
} from 'core/hooks';
import Button from 'pages/Dashboard/components/Button';
import {
  getErrorMessage,
} from 'utils/errors';
import isNil from 'lodash/isNil';
import {
  APP_ROUTES,
} from 'views/EMR/router';

export default function AuthorizeScreen() {
  const navigate = useNavigate();
  const { handleRedirectCallback } = useAuth();
  const [authError, setAuthError] = useState<ReactNode | string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const state = await handleRedirectCallback();
        navigate(state.appState.targetUrl ?? window.location.origin, {
          replace: true,
        });
      } catch (error) {
        const err = error as any;
        const errorCode = err?.error ?? err?.code;
        const messageText = err?.message ?? '';

        // Expected for silent auth (prompt=none) when there is no session.
        if (errorCode === 'login_required'
          || errorCode === 'consent_required'
          || errorCode === 'interaction_required') {
          navigate(APP_ROUTES.POPULATE_SUPPORT.ROOT, { replace: true });
          return;
        }

        // Stale /authorize from browser Back — leave quietly.
        if (errorCode === 'invalid_state'
          || errorCode === 'missing_transaction'
          || errorCode === 'state_mismatch'
          || /invalid state/i.test(messageText)) {
          navigate(APP_ROUTES.EMR.ROOT, { replace: true });
          return;
        }

        const message = getErrorMessage(error)
          ?? err?.error_description
          ?? err?.description
          ?? err?.message
          ?? 'Authorization failed.';
        const formatted = `${String(message).charAt(0).toUpperCase()}${String(message).slice(1)}`;
        setAuthError(formatted);
      }
    })();
  }, [handleRedirectCallback, navigate]);

  return !isNil(authError) ? (
    <div className="flex items-center justify-center min-h-screen p-6">
      <div className="max-w-md w-full bg-white shadow rounded p-6 text-center">
        <h1 className="text-lg font-semibold mb-3">Sign-in error</h1>
        <div className="text-sm text-red-700 bg-red-50 border rounded p-3 mb-4 font-medium">
          {authError}
        </div>
        <div className="text-xs text-gray-600 mb-6">
          Try again or contact your administrator.
        </div>
        <div className="flex gap-3 justify-center">
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={() => { navigate('/'); }}
            className="!min-w-[100px]"
          >
            Try Again
          </Button>
        </div>
      </div>
    </div>
  ) : null;
}
